import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { mockUser, mockTenant, mockContacts, mockCampaigns, mockTemplates, mockActions } from '../services/mockData';

const AppContext = createContext(null);

// Normalize campaign fields from backend (snake_case) to frontend (short names)
function normalizeCampaign(c) {
  return {
    ...c,
    contacts:        c.contacts        ?? c.target_count     ?? c.sent_count ?? 0,
    opens:           c.opens           ?? c.opened_count      ?? 0,
    clicks:          c.clicks          ?? c.clicked_count     ?? 0,
    feedback:        c.feedback        ?? c.feedback_count    ?? 0,
    open_rate:       c.open_rate       ?? 0,
    email_sent_count: c.email_sent_count ?? null,
    sms_sent_count:   c.sms_sent_count   ?? null,
  };
}

export function AppProvider({ children }) {
  // ── Auth state ─────────────────────────────────────────────────────────────
  const [token, setToken]   = useState(() => localStorage.getItem('appee_token'));
  const [user, setUser]     = useState(null);
  // Mode expert (Lot B) : navigation à deux vitesses, mémorisé par utilisateur.
  // Défaut true tant que l'utilisateur n'est pas chargé (évite le flash de menu
  // réduit chez les clients existants, tous grandfathered en expert).
  const [expertMode, setExpertModeState] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // ── Data state ─────────────────────────────────────────────────────────────
  const [contacts,        setContacts]        = useState([]);
  const [campaigns,       setCampaigns]       = useState([]);
  const [templates,       setTemplates]       = useState([]);
  const [actions,         setActions]         = useState([]);
  const [smtp,            setSmtp]            = useState(null);
  const [analytics,       setAnalytics]       = useState(null);
  const [dashboardData,   setDashboardData]   = useState(null);
  const [companyProfile,  setCompanyProfile]  = useState(() => {
    try { return JSON.parse(localStorage.getItem('appee_company_profile')) || {}; } catch { return {}; }
  });
  const [strategyInstances, setStrategyInstances] = useState(() => {
    try { return JSON.parse(localStorage.getItem('appee_strategies_v2')) || []; } catch { return []; }
  });
  const [personalProfile, setPersonalProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem('appee_personal_profile')) || {}; } catch { return {}; }
  });
  const [collaborators, setCollaborators] = useState(() => {
    try { return JSON.parse(localStorage.getItem('appee_collaborators')) || []; } catch { return []; }
  });

  // ── UI state ───────────────────────────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [quotaError, setQuotaError] = useState(null);
  const [autoSendEnabled, setAutoSendEnabled] = useState(true);

  // ── Reminders (synced to backend) ─────────────────────────────────────────
  const [reminders, setReminders] = useState([]);

  const showQuotaError = useCallback((err) => {
    if (err?.quotaExceeded) {
      setQuotaError({
        type:    err.quotaType,
        used:    err.quotaUsed,
        limit:   err.quotaLimit,
        message: err.message,
      });
    }
  }, []);

  const dismissQuotaError = useCallback(() => setQuotaError(null), []);

  const addNotification = useCallback((msg, type = 'success') => {
    const id = Date.now();
    setNotifications(n => [...n, { id, msg, type }]);
    setTimeout(() => setNotifications(n => n.filter(x => x.id !== id)), 5000);
  }, []);

  // ── Load all data after auth ───────────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      const [contactsRes, campaignsRes, templatesRes, actionsRes, analyticsRes, dashRes, remindersRes] = await Promise.allSettled([
        api.getContacts(),
        api.getCampaigns(),
        api.getTemplates(),
        api.getActionPlans(),
        api.getAnalytics(),
        api.dashboard(),
        api.getReminders(),
      ]);

      if (contactsRes.status === 'fulfilled') {
        setContacts(contactsRes.value?.data ?? contactsRes.value ?? []);
      }
      if (campaignsRes.status === 'fulfilled') {
        const raw = campaignsRes.value?.data ?? campaignsRes.value ?? [];
        setCampaigns(raw.map(normalizeCampaign));
      }
      if (templatesRes.status === 'fulfilled') {
        setTemplates(templatesRes.value ?? []);
      }
      if (actionsRes.status === 'fulfilled') {
        setActions(actionsRes.value ?? []);
      }
      if (remindersRes.status === 'fulfilled') {
        setReminders(remindersRes.value ?? []);
      }
      if (analyticsRes.status === 'fulfilled' && analyticsRes.value) {
        setAnalytics(analyticsRes.value);
      }

      // Dashboard stats — stored separately to avoid polluting analytics state
      if (dashRes.status === 'fulfilled' && dashRes.value) {
        const d = dashRes.value;
        setDashboardData(d);
        // Expose auto_send_enabled globally (used in Layout warning banner)
        setAutoSendEnabled(d.auto_send_enabled !== false);
        // Patch tenant with accurate limits (plan accessors not serialized in /auth/me)
        setTenant(t => t ? ({
          ...t,
          contacts_used:  d.usage?.contacts_used  ?? t.contacts_used,
          contacts_limit: d.usage?.contacts_limit ?? t.contacts_limit,
          emails_used:    d.usage?.emails_used    ?? t.emails_used,
          emails_limit:   d.usage?.emails_limit   ?? t.emails_limit,
          ai_used:        d.usage?.ai_used        ?? t.ai_used,
          ai_limit:       d.usage?.ai_limit       ?? t.ai_limit,
        }) : t);
      }

      // SMTP status
      try {
        const smtpData = await api.getSmtp();
        setSmtp(smtpData);
        if (!smtpData?.verified) {
          addNotification('📧 Tes campagnes partent via Appee (expéditeur noreply). Pour envoyer depuis TON adresse e-mail : Paramètres → Mon adresse d\'envoi (2 min).', 'warning');
        }
      } catch {}

      // Company profile
      try {
        const cp = await api.getCompany();
        if (cp && Object.keys(cp).length > 0) {
          setCompanyProfile(cp);
          localStorage.setItem('appee_company_profile', JSON.stringify(cp));
        }
      } catch {}

      // Strategy instances
      try {
        const si = await api.getStrategyInstances();
        if (Array.isArray(si)) {
          setStrategyInstances(si);
          localStorage.setItem('appee_strategies_v2', JSON.stringify(si));
        }
      } catch {}

      // Personal / family profile
      try {
        const pp = await api.getPersonalProfile();
        if (pp && Object.keys(pp).length > 0) {
          setPersonalProfile(pp);
          localStorage.setItem('appee_personal_profile', JSON.stringify(pp));
        }
      } catch {}

      // Collaborators
      try {
        const cols = await api.getCollaborators();
        if (Array.isArray(cols)) {
          setCollaborators(cols);
          localStorage.setItem('appee_collaborators', JSON.stringify(cols));
        }
      } catch {}

    } catch (err) {
      console.error('Failed to load app data:', err);
    }
  }, [addNotification]);

  // ── Startup: restore session ───────────────────────────────────────────────
  useEffect(() => {
    async function restoreSession() {
      const savedToken = localStorage.getItem('appee_token');
      if (!savedToken) { setAuthLoading(false); return; }

      // Effacer immédiatement les données cachées du tenant précédent —
      // elles seront rechargées par loadData() après validation du token.
      // Sans ça, un refresh de page affiche brièvement les données du dernier tenant connecté.
      if (localStorage.getItem('appee_demo') !== '1') {
        localStorage.removeItem('appee_company_profile');
        localStorage.removeItem('appee_strategies_v2');
        localStorage.removeItem('appee_personal_profile');
        localStorage.removeItem('appee_collaborators');
        setCompanyProfile({});
        setStrategyInstances([]);
        setPersonalProfile({});
        setCollaborators([]);
      }

      // Mode démo — pas besoin d'appeler l'API
      if (localStorage.getItem('appee_demo') === '1') {
        setUser(mockUser);
        setTenant(mockTenant);
        setContacts(mockContacts);
        setCampaigns(mockCampaigns);
        setTemplates(mockTemplates);
        setActions(mockActions);
        setAuthLoading(false);
        return;
      }

      try {
        const me = await api.me();
        setUser(me);
        if (typeof me?.expert_mode === 'boolean') setExpertModeState(me.expert_mode);
        setTenant(me.tenant);
        await loadData();
      } catch {
        localStorage.removeItem('appee_token');
        setToken(null);
      } finally {
        setAuthLoading(false);
      }
    }
    restoreSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auth actions ───────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    setSessionExpired(false);
    // Vider le cache du tenant précédent avant de charger le nouveau
    localStorage.removeItem('appee_company_profile');
    localStorage.removeItem('appee_strategies_v2');
    localStorage.removeItem('appee_personal_profile');
    localStorage.removeItem('appee_collaborators');
    setCompanyProfile({});
    setStrategyInstances([]);
    setPersonalProfile({});
    setCollaborators([]);
    const res = await api.login(email, password);
    localStorage.setItem('appee_token', res.token);
    setToken(res.token);
    setUser(res.user);
    setTenant(res.user.tenant);
    await loadData();
    return res;
  }, [loadData]);

  const register = useCallback(async (data) => {
    // Vider le cache du tenant précédent avant de charger le nouveau
    localStorage.removeItem('appee_company_profile');
    localStorage.removeItem('appee_strategies_v2');
    localStorage.removeItem('appee_personal_profile');
    localStorage.removeItem('appee_collaborators');
    setCompanyProfile({});
    setStrategyInstances([]);
    setPersonalProfile({});
    setCollaborators([]);
    const res = await api.register(data);
    localStorage.setItem('appee_token', res.token);
    setToken(res.token);
    setUser(res.user);
    setTenant(res.user.tenant);
    await loadData();
    return res;
  }, [loadData]);

  const logout = useCallback(async () => {
    const isDemo = localStorage.getItem('appee_demo') === '1';
    if (!isDemo) {
      try { await api.logout(); } catch {}
    }
    // Effacer le token
    localStorage.removeItem('appee_token');
    localStorage.removeItem('appee_demo');
    // Effacer TOUTES les données cachées par tenant — critique pour l'isolation multi-tenant
    localStorage.removeItem('appee_company_profile');
    localStorage.removeItem('appee_strategies_v2');
    localStorage.removeItem('appee_personal_profile');
    localStorage.removeItem('appee_collaborators');
    setToken(null);
    setUser(null);
    setTenant(null);
    setContacts([]);
    setCampaigns([]);
    setTemplates([]);
    setActions([]);
    setSmtp(null);
    setAnalytics(null);
    setCompanyProfile({});
    setStrategyInstances([]);
  }, []);

  // ── Inactivity timeout (10 min) ────────────────────────────────────────────
  const INACTIVITY_MS = 10 * 60 * 1000;
  const inactivityRef = useRef(null);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityRef.current) clearTimeout(inactivityRef.current);
    inactivityRef.current = setTimeout(() => {
      setSessionExpired(true);
      logout();
    }, INACTIVITY_MS);
  }, [logout]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAuth = !!token && !!user;
  useEffect(() => {
    if (!isAuth) {
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
      return;
    }
    const events = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetInactivityTimer, { passive: true }));
    resetInactivityTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetInactivityTimer));
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
    };
  }, [isAuth, resetInactivityTimer]);

  // ── Mode démo (sans backend) ───────────────────────────────────────────────
  const loginDemo = useCallback(() => {
    const demoToken = 'demo-mode';
    localStorage.setItem('appee_token', demoToken);
    localStorage.setItem('appee_demo', '1');
    setToken(demoToken);
    setUser(mockUser);
    setTenant(mockTenant);
    setContacts(mockContacts);
    setCampaigns(mockCampaigns);
    setTemplates(mockTemplates);
    setActions(mockActions);
    setSmtp(null);
  }, []);

  // ── Contact helpers ────────────────────────────────────────────────────────
  const updateContact = useCallback(async (id, data) => {
    const updated = await api.updateContact(id, data);
    setContacts(cs => cs.map(c => c.id === id ? { ...c, ...updated } : c));
    return updated;
  }, []);

  const deleteContact = useCallback(async (id) => {
    await api.deleteContact(id);
    setContacts(cs => cs.filter(c => c.id !== id));
  }, []);

  const addContact = useCallback(async (data) => {
    const created = await api.createContact(data);
    setContacts(cs => [created, ...cs]);
    return created;
  }, []);

  const refreshContacts = useCallback(async () => {
    const res = await api.getContacts();
    setContacts(res?.data ?? res ?? []);
  }, []);

  // ── Action helpers ─────────────────────────────────────────────────────────
  const dismissAction = useCallback(async (id) => {
    await api.dismissAction(id);
    setActions(as => as.map(a => a.id === id ? { ...a, status: 'dismissed' } : a));
  }, []);

  const launchAction = useCallback(async (id) => {
    await api.launchAction(id);
    setActions(as => as.map(a => a.id === id ? { ...a, status: 'launched' } : a));
    addNotification('Action lancée avec succès !');
  }, [addNotification]);

  // ── Company profile ────────────────────────────────────────────────────────
  const saveCompanyProfile = useCallback(async (data) => {
    try {
      const saved = await api.saveCompany(data);
      setCompanyProfile(saved);
      localStorage.setItem('appee_company_profile', JSON.stringify(saved));
      return saved;
    } catch {
      setCompanyProfile(data);
      localStorage.setItem('appee_company_profile', JSON.stringify(data));
      return data;
    }
  }, []);

  // ── Personal / family profile ──────────────────────────────────────────────
  const savePersonalProfileFn = useCallback(async (data) => {
    try {
      const saved = await api.savePersonalProfile(data);
      setPersonalProfile(saved);
      localStorage.setItem('appee_personal_profile', JSON.stringify(saved));
      return saved;
    } catch {
      setPersonalProfile(data);
      localStorage.setItem('appee_personal_profile', JSON.stringify(data));
      return data;
    }
  }, []);

  // ── SMTP health (derived from smtp state + manual refresh) ────────────────
  const smtpHealth = smtp ? {
    ok: smtp.verified === true,
    last_checked: smtp.last_tested_at ?? null,
    error: smtp.last_test_error ?? null,
  } : null;

  const refreshSmtpHealth = useCallback(async () => {
    try {
      const status = await api.checkSmtpHealth();
      return status;
    } catch {
      return { ok: false, error: 'Impossible de vérifier la connexion SMTP' };
    }
  }, []);

  // Bascule le mode expert (optimiste, rollback si l'API échoue)
  const toggleExpertMode = async () => {
    const next = !expertMode;
    setExpertModeState(next);
    try {
      await api.setExpertMode(next);
      addNotification(next ? '🎛️ Mode expert activé' : '😌 Mode simple activé');
    } catch (e) {
      setExpertModeState(!next);
      addNotification("Impossible d'enregistrer le mode — réessaie.", 'error');
    }
  };

  const value = {
    // Mode expert (Lot B)
    expertMode,
    toggleExpertMode,
    // Auth
    token,
    isAuthenticated: !!token && !!user,
    authLoading,
    login,
    logout,
    register,

    // Data
    user,   setUser,
    tenant, setTenant,
    contacts,  setContacts,  updateContact, deleteContact, addContact, refreshContacts,
    campaigns, setCampaigns,
    templates, setTemplates,
    actions,   setActions,   dismissAction, launchAction,
    smtp,      setSmtp,
    analytics, setAnalytics,
    dashboardData,
    companyProfile, saveCompanyProfile, setCompanyProfile,
    strategyInstances, setStrategyInstances,
    personalProfile, setPersonalProfile, savePersonalProfile: savePersonalProfileFn,
    collaborators, setCollaborators,

    // UI
    notifications,
    addNotification,
    sessionExpired,
    quotaError,
    showQuotaError,
    dismissQuotaError,
    autoSendEnabled,
    setAutoSendEnabled,

    // AI availability
    aiEnabled:     (tenant?.ai_used ?? 0) < (tenant?.ai_limit ?? 20),
    aiCreditsLeft: Math.max(0, (tenant?.ai_limit ?? 20) - (tenant?.ai_used ?? 0)),

    // SMTP health
    smtpHealth,
    refreshSmtpHealth,
    serverOnline: true,

    // Reminders
    reminders,
    setReminders,

    // Demo
    loginDemo,

    // Reload
    loadData,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
